import { useCallback, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useApiConfig } from "common-fe";
import { OrganizationApi, EntitlementsApi, Configuration } from "account-common";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// Product apps whose org→app licensing the super admin toggles (Layer 2).
// Distinct from the permission manifest (an org can license an app whose
// manifest isn't registered yet). Keep in sync as products ship.
export const PRODUCT_APPS = ["score", "manager"];

/**
 * Super-admin org administration data (AUTHZ.md §9.1). All calls go through the
 * generated OpenAPI client (never direct fetch):
 *   OrganizationApi.listAllOrganizations  — GET /org/all (enriched, unfiltered here —
 *                                            this page manages the flag so it needs
 *                                            every org, not just the switcher's subset)
 *   OrganizationApi.provisionOrganization — POST /org/provision
 *   OrganizationApi.updateOrganization    — POST /org/:id (superadminEnabled, #2074)
 *   EntitlementsApi.setOrgEntitlements    — PUT /org/:id/entitlements
 */
export function useSuperOrgs() {
  const auth = useAuth();
  const buildConfig = useApiConfig();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = useCallback(() => {
    const accessToken = auth.user?.access_token;
    if (!accessToken) return null;
    return new Configuration(buildConfig(ACCOUNT_API_BASE_URL));
  }, [auth.user?.access_token, buildConfig]);

  const fetchAll = useCallback(async () => {
    const cfg = config();
    if (!cfg) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await new OrganizationApi(cfg).listAllOrganizations();
      if (res.data?.status === "success" && Array.isArray(res.data.data)) {
        setOrgs(res.data.data);
      } else {
        setError(new Error("Failed to load organizations"));
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const provision = useCallback(
    async ({ name, ownerEmail, entitlements }) => {
      const cfg = config();
      const res = await new OrganizationApi(cfg).provisionOrganization({
        name,
        ownerEmail,
        entitlements,
      });
      await fetchAll();
      return res.data?.data?.organization;
    },
    [config, fetchAll]
  );

  const setEntitlements = useCallback(
    async (orgId, apps) => {
      const cfg = config();
      const res = await new EntitlementsApi(cfg).setOrgEntitlements(orgId, { apps });
      // Optimistically reflect the change locally.
      setOrgs((prev) =>
        prev.map((o) => (o.id === orgId ? { ...o, entitlements: apps } : o))
      );
      return res.data?.data?.entitlements;
    },
    [config]
  );

  const setSuperadminEnabled = useCallback(
    async (orgId, enabled) => {
      const cfg = config();
      const res = await new OrganizationApi(cfg).updateOrganization(orgId, {
        superadminEnabled: enabled,
      });
      // Optimistically reflect the change locally.
      setOrgs((prev) =>
        prev.map((o) => (o.id === orgId ? { ...o, superadminEnabled: enabled } : o))
      );
      return res.data?.data?.organization;
    },
    [config]
  );

  return {
    orgs,
    loading,
    error,
    refetch: fetchAll,
    provision,
    setEntitlements,
    setSuperadminEnabled,
  };
}
